import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import en from '../locales/en';
import cs from '../locales/cs';

const translations = { en, cs };

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('fleetora_language');
    if (saved && (saved === 'en' || saved === 'cs')) {
      return saved;
    }
    // Default to English
    return 'en';
  });

  useEffect(() => {
    // Save to localStorage whenever language changes
    localStorage.setItem('fleetora_language', language);
  }, [language]);

  const t = translations[language] || translations.en;

  const switchLanguage = useCallback((lang) => {
    if (lang === 'en' || lang === 'cs') {
      setLanguage(lang);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'cs' : 'en');
  }, []);

  // Helper function to get nested translation by key path
  // e.g., translate('nav.flightOps') returns 'FlightOps' or 'Operace'
  // Supports interpolation: translate('key', { name: 'John' }) replaces {name} with 'John'
  const translate = useCallback((keyPath, paramsOrFallback = '') => {
    const keys = keyPath.split('.');
    let result = t;
    
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        return typeof paramsOrFallback === 'string' ? (paramsOrFallback || keyPath) : keyPath;
      }
    }
    
    let finalResult = result || (typeof paramsOrFallback === 'string' ? paramsOrFallback : '') || keyPath;
    
    // Handle interpolation if params object is provided
    if (typeof paramsOrFallback === 'object' && paramsOrFallback !== null) {
      Object.entries(paramsOrFallback).forEach(([key, value]) => {
        finalResult = finalResult.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
      });
    }
    
    return finalResult;
  }, [t]);

  const value = useMemo(() => ({ 
    language, 
    setLanguage: switchLanguage, 
    toggleLanguage,
    t,
    translate 
  }), [language, t, translate, switchLanguage, toggleLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
